import { Divider, Fab, Grid, Tab, Tabs, useMediaQuery } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { Add } from '@mui/icons-material'
import Box from '@mui/material/Box'
import { isEqual } from 'lodash'
import i18n from 'locales/i18n'

import useCustomersQuery, { useEditCustomerMutation } from 'shared/store/useCustomers'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import { MarkupThresholdType } from 'shared/store/useLocations/types'
import { CustomerType } from 'shared/store/useCustomers/types'

import { sortAlphabetical } from 'shared/utils/helpers/sorters'
import UnsavedChangesModal from './components/UnsavedChangesModal'
import StyledCustomerTab, { TabContainer } from './styles'
import ClientData from './components/ClientData'
import { RootStyle } from '../Locations/styles'
import Contacts from './components/Contacts'
import Vehicles from './components/Vehicles'
import { ModalDetailsType } from './types'
import Markup from './components/Markup'
import { pl, en, de } from './locales'
import Modal from './components/Modal'
import { schema } from './schema'

const tabs: { label: string; value: string }[] = [
   {
      label: 'CUSTOMERS:clientData',
      value: 'clientData',
   },
   {
      label: 'CUSTOMERS:contacts',
      value: 'contacts',
   },
   {
      label: 'CUSTOMERS:vehicles',
      value: 'vehicles',
   },
   {
      label: 'CUSTOMERS:markup',
      value: 'markup',
   },
]
export default function Customers() {
   i18n.addResourceBundle('pl-PL', 'CUSTOMERS', pl)
   i18n.addResourceBundle('en-EN', 'CUSTOMERS', en)
   i18n.addResourceBundle('de-DE', 'CUSTOMERS', de)
   const { t } = useTranslation('CUSTOMERS')
   const smallerScreen = useMediaQuery((theme) => theme.breakpoints.down('lg'))
   const [searchParams, setSearchParams] = useSearchParams()
   const [modalDetails, setModalDetails] = useState<ModalDetailsType>({
      open: false,
      variant: undefined,
      customerDetails: undefined,
   })
   const navigate = useNavigate()
   const [openUnsavedChangesModal, setOpenUnsavedChangesModal] = useState<{
      open: boolean
      customer?: CustomerType
   }>({ open: false, customer: undefined })
   const [activeCustomer, setActiveCustomer] = useState<CustomerType | undefined>(undefined)
   const [activeTab, setActiveTab] = useState<'clientData' | 'vehicles' | 'contacts' | 'markup'>(
      (searchParams.get('tab') as 'clientData' | 'vehicles' | 'contacts' | 'markup') ?? 'clientData'
   )
   const { data } = useCustomersQuery()
   const { addNotification } = useNotificationContext()
   const { mutate: editCustomer } = useEditCustomerMutation()
   const [thresholds, setThresholds] = useState<MarkupThresholdType[]>([])
   const [priceType, setPriceType] = useState<'NET' | 'GROSS'>('NET')

   const formik = useFormik<CustomerType>({
      initialValues: {
         name: '',
         surname: '',
         uuid: '',
         displayName: '',
         companyName: '',
         contacts: [],
         taxID: '',
         deliveryAddresses: [],
         deliveryType: '',
         paymentMethod: '',
         address: {
            street: '',
            houseNumber: '',
            zipCode: '',
            city: '',
            country: 'Polska',
            voivodeship: '',
            county: '',
         },
         productsMarkupSettings: {
            priceType: 'GROSS',
            thresholds: [],
         },
      },
      onSubmit: (values) => {
         if ((!!values.name && !!values.surname) || (!!values.taxID && !!values.companyName)) {
            editCustomer(
               {
                  payload: {
                     ...values,
                     paymentMethod: values.paymentMethod ? values.paymentMethod : null,
                     deliveryType: values.deliveryType ? values.deliveryType : null,
                  },
                  customerUuid: values.uuid,
               },
               {
                  onSuccess: () => addNotification('successSave', 'success'),
               }
            )
         } else {
            addNotification(t('CUSTOMERS:noCompanyOrCustomerDetailsErrorEdit'), 'error')
         }
      },
      validationSchema: schema,
   })

   const { values, setValues } = formik

   function renderTab() {
      switch (activeTab) {
         case 'clientData':
            return <ClientData setModalDetails={setModalDetails} customer={activeCustomer} />
         case 'contacts':
            return <Contacts customerDetails={activeCustomer} />
         case 'vehicles':
            return <Vehicles customerUuid={activeCustomer?.uuid} />
         case 'markup':
            return (
               <Markup
                  priceType={priceType}
                  setPriceType={setPriceType}
                  thresholds={thresholds}
                  setThresholds={setThresholds}
                  customerDetails={activeCustomer!}
               />
            )
         default:
            return null
      }
   }

   useEffect(() => {
      if (data && data.length > 0) {
         const uuid = searchParams.get('customerUuid')
         if (uuid) {
            const customer = data?.find((cust) => cust.uuid === uuid)
            if (customer) {
               setActiveCustomer(customer)
            }
         } else {
            setActiveCustomer(data[0])
         }
      }
   }, [data, searchParams.get('customerUuid')])

   useEffect(() => {
      if (activeCustomer) {
         searchParams.set('customerUuid', activeCustomer.uuid)
         setSearchParams(searchParams)
         setValues({ ...activeCustomer })
         setThresholds(
            activeCustomer?.productsMarkupSettings.thresholds
               ? activeCustomer.productsMarkupSettings.thresholds.slice(0, -1)
               : []
         )
         setPriceType(activeCustomer.productsMarkupSettings.priceType ?? 'NET')
      }
   }, [activeCustomer])

   useEffect(() => {
      searchParams.set('tab', activeTab)
      setSearchParams(searchParams)
   }, [activeTab])

   useEffect(() => {
      if (searchParams.get('new')) {
         setModalDetails({ open: true, variant: 'NEW' })
      }
   }, [])

   return (
      <Box>
         <Typography variant="h4" color="primary" sx={{ fontWeight: 'normal', mb: 2 }}>
            {t('customersTitle')}
         </Typography>
         {data && data.length > 0 ? (
            <RootStyle>
               <Grid sx={{ width: '100%' }} container>
                  <Grid size={{ xs: 12, md: 2, lg: 2 }}>
                     <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 6, mb: 1 }}>
                        <Tabs sx={{ width: '100%' }} orientation="vertical" value={activeCustomer?.uuid}>
                           {data
                              ?.sort((a, b) => sortAlphabetical(a.displayName, b.displayName))
                              .map((customer) => (
                                 <StyledCustomerTab
                                    activeCustomerUuid={activeCustomer?.uuid ?? ''}
                                    customerUuid={customer.uuid}
                                    key={customer.uuid}
                                    value={customer.uuid}
                                    label={customer.displayName}
                                    onClick={() => {
                                       if (
                                          isEqual(activeCustomer, values) &&
                                          isEqual(
                                             thresholds,
                                             activeCustomer?.productsMarkupSettings.thresholds.slice(0, -1)
                                          ) &&
                                          priceType === activeCustomer?.productsMarkupSettings.priceType
                                       ) {
                                          setActiveCustomer(customer)
                                       } else {
                                          setOpenUnsavedChangesModal({ open: true, customer })
                                       }
                                    }}
                                 />
                              ))}
                           <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                              <Fab
                                 sx={{ width: 35, height: 35 }}
                                 color="primary"
                                 onClick={() => setModalDetails({ open: true, variant: 'NEW' })}
                              >
                                 <Add sx={{ color: 'white', fontSize: 26 }} />
                              </Fab>
                           </Box>
                        </Tabs>
                     </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 10, lg: 10 }}>
                     <Box sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
                        <Tabs
                           variant={smallerScreen ? 'scrollable' : 'standard'}
                           centered
                           sx={{ display: 'flex', justifyContent: 'center' }}
                           value={activeTab}
                           onChange={(e, value) => setActiveTab(value)}
                        >
                           {tabs.map((tab) => (
                              <Tab key={tab.value} sx={{ fontSize: 12 }} value={tab.value} label={t(tab.label)} />
                           ))}
                        </Tabs>
                        <Divider flexItem />
                     </Box>
                     <FormikProvider value={formik}>
                        <TabContainer>{renderTab()}</TabContainer>
                     </FormikProvider>
                  </Grid>
               </Grid>
            </RootStyle>
         ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
               <Typography variant="h5"> {t('CUSTOMERS:noCustomers')}</Typography>
               <OutlinedButton onClick={() => setModalDetails({ open: true, variant: 'NEW' })} variant="outlined">
                  {t('CUSTOMERS:addCustomer')}
               </OutlinedButton>
            </Box>
         )}
         <Modal
            setFirstCustomer={() => {
               if (data) {
                  setActiveCustomer(data[0])
               }
            }}
            setModalDetails={setModalDetails}
            modalDetails={modalDetails}
            onClose={(withRedirect?: boolean, customerUuid?: string) => {
               setModalDetails({ open: false, customerDetails: undefined, variant: undefined })
               if (withRedirect) {
                  setTimeout(() => {
                     navigate(`/dashboard/main-cart?customer=${customerUuid}`)
                  }, 1000)
               }
            }}
         />
         <UnsavedChangesModal
            open={openUnsavedChangesModal.open}
            onClose={() => {
               setOpenUnsavedChangesModal({ open: false, customer: undefined })
            }}
            onSubmit={() => {
               setActiveCustomer(openUnsavedChangesModal.customer!)
               setOpenUnsavedChangesModal({ open: false, customer: undefined })
            }}
         />
      </Box>
   )
}
